/* Solar Black Belt original procedural scenes. Adapted for React lifecycle use. */
import * as THREE from "three";

const palette = { ground: 0xdcece5, body: 0xf4f5ed, roof: 0x8faaa4, panel: 0x103d5a, trim: 0x99c5d2, lime: 0xc5ed73, green: 0x259781, dark: 0x123f40 };

export function createSolarScene(host, kind = "commercial", options = {}) {
  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  } catch (e) {
    try {
      renderer = new THREE.WebGLRenderer({ antialias: false, alpha: true });
    } catch (e2) {
      throw new Error("WebGL unavailable");
    }
  }
  renderer.setPixelRatio(Math.min(devicePixelRatio, 1.7));
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  host.appendChild(renderer.domElement);
  renderer.domElement.setAttribute("aria-label", kind + " interactive energy concept");
  renderer.domElement.setAttribute("role", "img");
  const scene = new THREE.Scene(),
    camera = new THREE.PerspectiveCamera(35, 1, 0.1, 100);
  camera.position.set(11, 10, 13);
  camera.lookAt(0, 0, 0);
  if (options.heroSky) {
    // Hero framing: lift the view so bright sky fills the upper half of the frame.
    camera.position.set(11.5, 10.5, 13.5);
    camera.lookAt(0, 3.2, 0);
  }
  const hemi = new THREE.HemisphereLight(0xffffff, 0x60817b, 2.5);
  scene.add(hemi);
  const sun = new THREE.DirectionalLight(0xfff7d9, 3.4);
  sun.position.set(-7, 14, 7);
  sun.castShadow = true;
  sun.shadow.mapSize.set(1024, 1024);
  sun.shadow.camera.left = -12;
  sun.shadow.camera.right = 12;
  sun.shadow.camera.top = 12;
  sun.shadow.camera.bottom = -12;
  scene.add(sun);
  let group = new THREE.Group(),
    pivots = [],
    panels = [],
    particles = [];
  scene.add(group);
  let state = { kind, coverage: 65, length: 120, width: 80, batteries: 2, night: false, showPanels: true, ...options },
    paused = false,
    visible = true,
    drag = false,
    lastX = 0,
    targetAngle = -0.18,
    frame;
  function box(w, h, d, x, y, z, color, root = group) {
    let m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), new THREE.MeshStandardMaterial({ color, roughness: 0.6, metalness: color === palette.panel ? 0.35 : 0.08 }));
    m.position.set(x, y, z);
    m.castShadow = true;
    m.receiveShadow = true;
    root.add(m);
    return m;
  }
  function panel(x, y, z, w = 1, d = 1.6, roofGroup = group) {
    let p = new THREE.Group();
    roofGroup.add(p);
    p.position.set(x, y, z);
    box(w, 0.07, d, 0, 0, 0, palette.panel, p);
    for (let j = -1; j <= 1; j++) box(0.012, 0.012, d, (j * w) / 3, 0.043, 0, palette.trim, p);
    for (let k = -2; k <= 2; k++) box(w, 0.012, 0.012, 0, 0.043, (k * d) / 6, palette.trim, p);
    panels.push(p);
    return p;
  }
  function battery(x, z, n = 1) {
    for (let i = 0; i < n; i++) {
      box(0.7, 1.65, 0.38, x + i * 0.88, 1.1, z, 0xf7faf9);
      box(0.38, 0.065, 0.405, x + i * 0.88, 1.65, z, palette.green);
      box(0.28, 0.025, 0.41, x + i * 0.88, 1.84, z, 0x233b3b);
    }
  }
  function turbine(x, z) {
    box(1.7, 1.2, 1.5, x, 0.8, z, 0xd1dfd8);
    let rotor = new THREE.Group();
    rotor.position.set(x, 1.43, z);
    group.add(rotor);
    for (let i = 0; i < 4; i++) {
      let blade = box(0.65, 0.03, 0.19, 0, 0, 0, palette.dark, rotor);
      blade.position.x = 0.27;
      blade.rotation.y = (i * Math.PI) / 2;
      let pivot = new THREE.Group();
      rotor.remove(blade);
      pivot.add(blade);
      pivot.rotation.y = (i * Math.PI) / 2;
      rotor.add(pivot);
    }
    pivots.push(rotor);
  }
  function tree(x, z) {
    box(0.12, 0.65, 0.12, x, 0.5, z, 0x7d7764);
    let c = new THREE.Mesh(new THREE.SphereGeometry(0.5, 12, 12), new THREE.MeshStandardMaterial({ color: 0x549f86 }));
    c.position.set(x, 1.1, z);
    group.add(c);
  }
  function clean() {
    group.traverse((o) => {
      if (o.geometry) o.geometry.dispose();
      if (o.material) o.material.dispose();
    });
    scene.remove(group);
    group = new THREE.Group();
    scene.add(group);
    pivots = [];
    panels = [];
    particles = [];
  }
  function build() {
    clean();
    box(12, 0.22, 10, 0, -0.25, 0, palette.ground);
    box(11, 0.035, 1.4, 0, -0.11, 3.7, 0xbacac5);
    for (let i = -4; i < 5; i += 2) box(0.8, 0.02, 0.04, i, -0.08, 3.7, 0xf4f5ed);
    let k = state.kind;
    const home = ["home", "solar-roof", "roofing", "construction", "insulation"].includes(k);
    let w = home ? 4.6 : 7.5,
      d = home ? 3.8 : 5.3;
    if (k === "model") {
      let ratio = Math.sqrt(state.length / state.width);
      w = Math.max(3, Math.min(9, 6 * ratio));
      d = Math.max(3, Math.min(7, 6 / ratio));
    }
    if (["powerwall", "commercial-storage", "smart-panel"].includes(k)) {
      box(7, 3, 0.3, 0, 1.4, -1.4, palette.body);
      box(7, 0.08, 4, 0, 0, 0, 0xbdcdc8);
      if (k === "smart-panel") {
        box(1.5, 2, 0.28, 0, 1.4, -1.1, palette.dark);
        for (let i = 0; i < 6; i++) box(0.35, 0.1, 0.32, i % 2 ? -0.3 : 0.3, 0.8 + Math.floor(i / 2) * 0.4, -0.9, palette.lime);
      } else battery(-1.5, -1, Math.min(state.batteries, 5));
    } else if (["ev", "supercharger"].includes(k)) {
      for (let x = -2; x <= 2; x += 2) {
        box(0.75, 1.8, 0.4, x, 0.9, -0.8, 0xf8faf6);
        box(0.35, 1.1, 0.45, x, 1, -0.8, k === "supercharger" ? 0xca3e43 : palette.dark);
        box(1.1, 0.5, 2, x, 0.42, 1.1, 0x406c78);
        box(0.85, 0.35, 1, x, 0.81, 1.1, 0xa8c8cd);
      }
    } else if (k === "hvac") {
      turbine(-1.5, 0);
      turbine(1.4, 0);
      for (let i = 0; i < 4; i++) box(6, 0.02, 0.03, 0, 0.1, -1.8 + i * 0.3, palette.green);
    } else if (k === "care") {
      box(6, 2, 4, 0, 1, 0, palette.body);
      for (let x = -2; x <= 2; x += 1.4) panel(x, 2.1, 0, 1.2, 2.3);
      let scan = box(6, 0.08, 0.1, 0, 2.25, 0, palette.lime);
      pivots.push(scan);
    } else {
      box(w, 2, d, 0, 0.95, 0, palette.body);
      box(w + 0.18, 0.15, d + 0.18, 0, 2, 0, palette.roof);
      for (let x = -w / 2 + 0.5; x < w / 2; x += 1.1) box(0.65, 0.65, 0.03, x, 0.94, d / 2 + 0.02, palette.dark);
      box(0.85, 1.3, 0.035, w / 2 - 0.8, 0.62, d / 2 + 0.04, 0xb3c7ba);
      if (home) {
        let roof = new THREE.Mesh(new THREE.ConeGeometry(3.9, 1.5, 4), new THREE.MeshStandardMaterial({ color: k === "roofing" ? 0x4a6f69 : 0x889e94 }));
        roof.rotation.y = Math.PI / 4;
        roof.scale.z = 0.82;
        roof.position.y = 2.6;
        roof.castShadow = true;
        group.add(roof);
        if (k === "insulation") box(4.1, 0.32, 3.2, 0, 2.16, 0, 0xf0d79e);
        if (k === "home" || k === "solar-roof") {
          for (let x = -1.5; x <= 1.5; x += 1)
            for (let z = 0.1; z <= 1.5; z += 0.65) {
              let p = panel(x, 2.9 - z * 0.42, z, 0.88, 0.58);
              p.rotation.x = -0.38;
            }
        }
        if (k === "construction") {
          for (let x = -2; x <= 2; x += 1) box(0.09, 3, 0.09, x, 1.5, -1.6, palette.dark);
          box(6, 0.12, 0.12, 0, 3, -1.6, palette.dark);
        }
      } else {
        let cols = Math.max(2, Math.floor(w / 1.1)),
          rows = Math.max(2, Math.floor(d / 1.3));
        let limit = Math.round((cols * rows * state.coverage) / 100),
          i = 0;
        for (let x = 0; x < cols; x++)
          for (let z = 0; z < rows; z++) {
            if (i++ >= limit) continue;
            panel(-w / 2 + 0.65 + x * 1.1, 2.17, -d / 2 + 0.7 + z * 1.3, 0.98, 1.17);
          }
        box(1, 0.4, 0.7, w / 2 - 1, 2.35, -d / 2 + 0.7, 0xcbd5cb);
      }
      if (state.batteries > 0) battery(w / 2 + 0.5, 1, Math.min(state.batteries, 3));
    }
    tree(-4.5, -2.8);
    tree(4.7, 2);
    tree(-4.8, 1.6);
    const curve = new THREE.CatmullRomCurve3([new THREE.Vector3(-2, 3, 0), new THREE.Vector3(0, 3.8, 0), new THREE.Vector3(4, 2, 0), new THREE.Vector3(4, 0.7, 2)]);
    for (let i = 0; i < 9; i++) {
      let p = new THREE.Mesh(new THREE.SphereGeometry(0.065, 8, 8), new THREE.MeshBasicMaterial({ color: palette.lime }));
      group.add(p);
      particles.push({ mesh: p, curve, offset: i / 9 });
    }
    panels.forEach((p) => (p.visible = state.showPanels));
    group.rotation.y = targetAngle;
  }
  build();
  const resize = () => {
    let w = host.clientWidth,
      h = host.clientHeight;
    if (!w || !h) return;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  };
  const resizeObserver = new ResizeObserver(resize);
  resizeObserver.observe(host);
  resize();
  host.addEventListener("pointerdown", (e) => {
    drag = true;
    lastX = e.clientX;
    host.setPointerCapture(e.pointerId);
  });
  host.addEventListener("pointerup", () => (drag = false));
  host.addEventListener("pointermove", (e) => {
    if (drag) {
      targetAngle += (e.clientX - lastX) * 0.007;
      lastX = e.clientX;
    }
  });
  const observer = new IntersectionObserver((es) => (visible = es[0].isIntersecting));
  observer.observe(host);
  const reduce = matchMedia("(prefers-reduced-motion: reduce)");
  let reduced = reduce.matches;
  reduce.addEventListener("change", (e) => (reduced = e.matches));
  function render(t) {
    frame = requestAnimationFrame(render);
    if (!visible || document.hidden) return;
    let time = t * 0.001;
    if (!paused && !reduced) {
      group.rotation.y += (targetAngle + Math.sin(time * 0.2) * 0.045 - group.rotation.y) * 0.08;
      pivots.forEach((p) => {
        if (state.kind === "care") p.position.z = Math.sin(time) * 1.7;
        else p.rotation.y = time;
      });
      particles.forEach((p) => p.mesh.position.copy(p.curve.getPoint((time * 0.15 + p.offset) % 1)));
    }
    renderer.render(scene, camera);
  }
  frame = requestAnimationFrame(render);
  return {
    update(next) {
      let rebuild = Object.keys(next).some((k) => !["paused", "night"].includes(k));
      Object.assign(state, next);
      if ("paused" in next) paused = next.paused;
      if ("night" in next) {
        hemi.intensity = next.night ? 0.45 : 2.5;
        sun.intensity = next.night ? 0.5 : 3.4;
      }
      if (rebuild) build();
    },
    dispose() {
      cancelAnimationFrame(frame);
      observer.disconnect();
      resizeObserver.disconnect();
      renderer.dispose();
      clean();
      renderer.domElement.remove();
    },
  };
}
